import getTravelDate from '@stencil/flights-date-utils/src/get-travel-date';
import isValidDate from '@stencil/date-utils/src/isValidDate';

export const getSearchParams = () => {
  return window.location.search;
};

export function urlSearchParamsToObject(urlSearchParam) {
  const entries = urlSearchParam.entries();
  let result = {};
  for (let entry of entries) {
    // each 'entry' is a [key, value] tupple
    const [key, value] = entry;
    result[key] = value;
  }
  return result;
}

export function searchParamObjectToUrl(urlSearchParam) {
  if (!urlSearchParam) {
    return '';
  }
  let result = '';
  let i = 0;
  for (let key in urlSearchParam) {
    // each 'entry' is a [key, value] tupple
    const value = urlSearchParam[key];
    result += `${i > 0 ? '&' : ''}${key}=${encodeURIComponent(value)}`;
    i++;
  }
  return result ? `?${result}` : '';
}

export const searchStringToObject = searchString => {
  const urlParams = new URLSearchParams(searchString);
  const paramObject = urlSearchParamsToObject(urlParams);

  return paramObject;
};

export function getDisplayTime(time) {
  const displayTime = ('0' + (time / 100).toFixed(2)).replace('.', ':');
  return displayTime.substring(displayTime.length - 5);
}

export function getDurationForDisplay(dur) {
  return ('0' + (dur / 100).toFixed(2)).replace('.', 'h ').substring(1) + 'm';
}

export function getTravelClass(travelClass) {
  switch (travelClass) {
    case 'B':
      return 'Business';
    case 'F':
      return 'First Class';
    case 'W':
      return 'Premium Economy';
    default:
      return 'Economy';
  }
}

export function getPaxType(dateOfBirth, dateOfTravel = new Date()) {
  if (dateOfBirth) {
    try {
      const dateArray = dateOfBirth.split('-');
      const dob = new Date(dateArray[2], dateArray[1] - 1, dateArray[0]);
      const difference = dateOfTravel.getTime() - dob.getTime();
      const age = Math.floor(difference / (1000 * 60 * 60 * 24 * 365.25));

      if (age >= 12) {
        return 'adult';
      } else if (age < 12 && age >= 2) {
        return 'child';
      } else if (age < 2 && age >= 0) {
        return 'infant';
      } else {
        return 'adult';
      }
    } catch {
      return 'adult';
    }
  } else {
    return 'adult';
  }
}

const getDateDifferenceForIsGreaterDate = (date, otherDate) => {
  const dateArray = date.split('-');
  const firstDate = new Date(dateArray[2], dateArray[1] - 1, dateArray[0]);

  const otherDateArray = otherDate.split('-');
  const secondDate = new Date(otherDateArray[0], otherDateArray[1] - 1, otherDateArray[2]);

  const difference = firstDate.getTime() - secondDate.getTime();
  return difference;
};

export function isGreaterThanDate(date, otherDate) {
  const difference = getDateDifferenceForIsGreaterDate(date, otherDate);
  return difference > 0;
}

export function isGreaterThanOrEqualDate(date, otherDate) {
  const difference = getDateDifferenceForIsGreaterDate(date, otherDate);
  return difference >= 0;
}

export const fieldNameMap = {
  FIRST_NAME: 'givenname',
  LAST_NAME: 'lastname',
  GENDER: 'title',
  DOB: 'dob',
  NATIONALITY: 'nationality',
  PASSPORT_DATE_OF_EXPIRY: 'passport_expiry',
  PASSPORT_ISSUING_COUNTRY: 'passport_issuing_country',
  PASSPORT_NUMBER: 'passport_num',
  FREQUENT_FLIER_NUM: 'frequent_flier_no',
  FREQUENT_FLIER_AIRLINE: 'frequent_flier_airline',
  DOCUMENT_ID: 'document_id',
  DOCUMENT_TYPE: 'document_type'
};

const ageErrors = {
  adult: `Adult's age must be more than 12 years old on the date of travel`,
  child: `Child's age must be less than 12 years and greater than 2 years on the date of travel`,
  infant: `Infant's age cannot be greater than 2 years on the date of travel`
};

const titleValues = {
  adult: ['Mr', 'Ms', 'Mrs'],
  child: ['Ms', 'Master'],
  infant: ['Ms', 'Master']
};

export const validateTraveller = (travellerDetails, paxType, rowFields, grpFields = {}) => {
  const errors = {};
  if (!rowFields) {
    return errors;
  }
  Object.keys(rowFields).forEach(field => {
    const regExp = rowFields[field].props.regex?.replaceAll(/\s/g, '');
    const re = new RegExp(regExp);
    if (
      travellerDetails[fieldNameMap[field]] !== null &&
      travellerDetails[fieldNameMap[field]] !== undefined &&
      rowFields[field].inputType !== 'DATE'
    ) {
      if (
        rowFields[field].props.max &&
        travellerDetails[fieldNameMap[field]].length > rowFields[field].props.max
      ) {
        errors[
          fieldNameMap[field]
        ] = `${rowFields[field].label} can't be more than ${rowFields[field].props.max} characters`;
        return;
      }
      if (travellerDetails[fieldNameMap[field]].length < rowFields[field].props.min) {
        if (travellerDetails[fieldNameMap[field]].length > 0) {
          errors[
            fieldNameMap[field]
          ] = `${rowFields[field].label} can't be less than ${rowFields[field].props.min} characters`;
        } else if (!rowFields[field].props.isOptional) {
          errors[fieldNameMap[field]] = `${rowFields[field].label} is mandatory`;
        }
        return;
      }
      if (rowFields[field].props.min !== '0' || travellerDetails[fieldNameMap[field]]) {
        if (!re.test(travellerDetails[fieldNameMap[field]])) {
          errors[fieldNameMap[field]] = `Please enter valid ${rowFields[field].label}`;
        }
      }
      if (
        (field === 'PASSPORT_ISSUING_COUNTRY' || field === 'NATIONALITY') &&
        !travellerDetails[fieldNameMap[field]]
      ) {
        // Country field manditory
        errors[fieldNameMap[field]] = `${rowFields[field].label} is mandatory`;
      }
      if (field === 'GENDER' && travellerDetails[fieldNameMap[field]]) {
        if (!titleValues[paxType].includes(travellerDetails[fieldNameMap[field]])) {
          errors[fieldNameMap[field]] = 'Please add a valid Title';
        }
      }
    } else if (rowFields[field].inputType === 'DATE') {
      if (travellerDetails[fieldNameMap[field]]) {
        const dateArray = travellerDetails[fieldNameMap[field]].split('-');
        const date = new Date(dateArray[2], dateArray[1] - 1, dateArray[0]);
        const isValidDateFlag = isValidDate(
          date,
          Number(dateArray[2]),
          Number(dateArray[1] - 1),
          Number(dateArray[0])
        );
        if (!isValidDateFlag) {
          errors[fieldNameMap[field]] = `Please enter a valid date`;
          return;
        }
        if (isGreaterThanDate(travellerDetails[fieldNameMap[field]], rowFields[field].props.max)) {
          const maxDateArr = rowFields[field].props.max.split('-');
          const newMaxDateString = `${maxDateArr[2]}-${maxDateArr[1]}-${maxDateArr[0]}`;
          errors[
            fieldNameMap[field]
          ] = `${rowFields[field].label} can't be after ${newMaxDateString}`;
          return;
        }
        if (
          !isGreaterThanOrEqualDate(
            travellerDetails[fieldNameMap[field]],
            rowFields[field].props.min
          )
        ) {
          const err = field === 'DOB' ? ageErrors[paxType] : '';
          errors[fieldNameMap[field]] = err
            ? err
            : `${rowFields[field].label} can't be before ${rowFields[field].props.min}`;
          return;
        }
      } else {
        errors[fieldNameMap[field]] = `${rowFields[field].label} is mandatory`;
      }
    }
  });
  if (travellerDetails.title.length === 0) {
    errors['title'] = 'Title is mandatory';
  }
  Object.keys(grpFields).forEach(field => {
    if (field === 'FREQUENT_FLIER_DETAIL') {
      let hasFFno = false;
      let hasFFairline = false;
      const frequentFlier = grpFields[field].formGroups[0]?.formGroup;

      const reFFno = new RegExp(frequentFlier.FREQUENT_FLIER_NUM.props.regex);
      const reFFairline = new RegExp(frequentFlier.FREQUENT_FLIER_AIRLINE.props.regex);
      if (travellerDetails[fieldNameMap['FREQUENT_FLIER_NUM']]) {
        hasFFno = true;
        if (!reFFno.test(travellerDetails[fieldNameMap['FREQUENT_FLIER_NUM']])) {
          errors[
            fieldNameMap['FREQUENT_FLIER_NUM']
          ] = `Please enter a valid ${frequentFlier.FREQUENT_FLIER_NUM.label}`;
        }
      }
      if (travellerDetails[fieldNameMap['FREQUENT_FLIER_AIRLINE']]) {
        hasFFairline = true;
        if (!reFFairline.test(travellerDetails[fieldNameMap['FREQUENT_FLIER_AIRLINE']])) {
          errors[
            fieldNameMap['FREQUENT_FLIER_AIRLINE']
          ] = `Please enter a valid ${frequentFlier.FREQUENT_FLIER_AIRLINE.label}`;
        }
      }
      if (!((hasFFno && hasFFairline) || (!hasFFno && !hasFFairline))) {
        const missingField = hasFFairline ? 'FREQUENT_FLIER_NUM' : 'FREQUENT_FLIER_AIRLINE';
        errors[fieldNameMap[missingField]] = `${
          hasFFairline
            ? frequentFlier.FREQUENT_FLIER_NUM.label
            : frequentFlier.FREQUENT_FLIER_AIRLINE.label
        } cannot be empty while ${
          hasFFairline
            ? frequentFlier.FREQUENT_FLIER_AIRLINE.label
            : frequentFlier.FREQUENT_FLIER_NUM.label
        } is entered`;
      }
    }
    if (field === 'TRAVELER_DOCUMENT') {
      const travellerDocument = grpFields[field].formGroups[0]?.formGroup;
      const reDocId = new RegExp(travellerDocument.DOCUMENT_ID.props.regex);
      const reDocType = new RegExp(travellerDocument.DOCUMENT_TYPE.props.regex);

      // Check Regex
      if (
        travellerDetails[fieldNameMap['DOCUMENT_ID']] &&
        !reDocId.test(travellerDetails[fieldNameMap['DOCUMENT_ID']])
      ) {
        errors[
          fieldNameMap['DOCUMENT_ID']
        ] = `Please enter a valid ${travellerDocument.DOCUMENT_ID.label}`;
      }
      if (
        travellerDetails[fieldNameMap['DOCUMENT_TYPE']] &&
        !reDocType.test(travellerDetails[fieldNameMap['DOCUMENT_TYPE']])
      ) {
        errors[
          fieldNameMap['DOCUMENT_TYPE']
        ] = `Please enter a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
      }
      // Check DOCUMENT Type'
      if (
        !travellerDocument.DOCUMENT_TYPE.props.isOptional &&
        !travellerDetails[fieldNameMap['DOCUMENT_TYPE']]?.length
      ) {
        errors[
          fieldNameMap['DOCUMENT_TYPE']
        ] = `Select a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
      }

      // Check Min Maximum --- default max 50
      if (
        (travellerDocument.DOCUMENT_ID.props.max &&
          travellerDetails[fieldNameMap['DOCUMENT_ID']].length >
            travellerDocument.DOCUMENT_ID.props.max) ||
        travellerDetails[fieldNameMap['DOCUMENT_ID']].length > 50
      ) {
        errors[
          fieldNameMap['DOCUMENT_ID']
        ] = `${travellerDocument.DOCUMENT_ID.label} can't be more than ${travellerDocument.DOCUMENT_ID.props.max} characters`;
        return;
      }
      if (
        travellerDetails[fieldNameMap['DOCUMENT_ID']].length <
        travellerDocument.DOCUMENT_ID.props.min
      ) {
        if (travellerDetails[fieldNameMap['DOCUMENT_ID']].length > 0) {
          errors[
            fieldNameMap['DOCUMENT_ID']
          ] = `${travellerDocument.DOCUMENT_ID.label} can't be less than ${travellerDocument.DOCUMENT_ID.props.min} characters`;
        } else if (!travellerDocument.DOCUMENT_ID.props.isOptional) {
          errors[
            fieldNameMap['DOCUMENT_ID']
          ] = `${travellerDocument.DOCUMENT_ID.label} is mandatory`;
        }
        return;
      }
    }
  });
  return errors;
};

export function validatePax(
  paxDetail,
  paxType,
  paxWiseTravellerFormDetails,
  dateOfTravel,
  isOptional = true
) {
  const paxTypeCap = paxType.toUpperCase();
  const errors = {};
  if (!paxWiseTravellerFormDetails) {
    return errors;
  }
  Object.keys(paxWiseTravellerFormDetails[paxTypeCap].rowFields).forEach(field => {
    const re = new RegExp(paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.regex);
    if (
      paxDetail[fieldNameMap[field]] !== null &&
      paxDetail[fieldNameMap[field]] !== undefined &&
      paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].inputType !== 'DATE'
    ) {
      if (
        paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max &&
        paxDetail[fieldNameMap[field]].length >
          paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max
      ) {
        errors[
          fieldNameMap[field]
        ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} can't be more than ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max} characters`;
        return;
      }
      if (
        paxDetail[fieldNameMap[field]].length <
        paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min
      ) {
        if (paxDetail[fieldNameMap[field]].length > 0) {
          errors[
            fieldNameMap[field]
          ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} can't be less than ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min} characters`;
        } else {
          errors[
            fieldNameMap[field]
          ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} is mandatory`;
        }
        return;
      }
      if (
        paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min !== '0' ||
        paxDetail[fieldNameMap[field]]
      ) {
        if (!re.test(paxDetail[fieldNameMap[field]])) {
          errors[
            fieldNameMap[field]
          ] = `Please enter valid ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label}`;
        }
      }
      if (
        (field === 'PASSPORT_ISSUING_COUNTRY' || field === 'NATIONALITY') &&
        !paxDetail[fieldNameMap[field]]
      ) {
        // Country field manditory
        errors[
          fieldNameMap[field]
        ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} is mandatory`;
      }
    } else if (paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].inputType === 'DATE') {
      if (paxDetail[fieldNameMap[field]]) {
        if (field !== 'DOB') {
          if (
            isGreaterThanDate(
              paxDetail[fieldNameMap[field]],
              paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max
            )
          ) {
            errors[
              fieldNameMap[field]
            ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} can't be after ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max}`;
            return;
          }
          if (
            !isGreaterThanDate(
              paxDetail[fieldNameMap[field]],
              paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min
            )
          ) {
            errors[
              fieldNameMap[field]
            ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} can't be before ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min}`;
            return;
          }
        }
      } else {
        errors[
          fieldNameMap[field]
        ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} is mandatory`;
      }
    }
  });
  // common Validations
  if (paxType !== getPaxType(paxDetail.dob, dateOfTravel)) {
    switch (paxType) {
      // case 'adult':
      //   errors['dob'] = `Adult's age must be more than 12 years old on the date of travel`;
      //   break;
      // case 'child':
      //   errors[
      //     'dob'
      //   ] = `Child's age must be less than 12 years and greater than 2 years on the date of travel`;
      //   break;
      case 'infant':
        errors['dob'] = `Age of Infant should be less than 2 years on the last date of travel`;
        break;
    }
  }
  if (paxDetail.title.length === 0) {
    errors['page_error'] = 'Title is mandatory';
  }
  const { grpFields = {} } = paxWiseTravellerFormDetails[paxTypeCap];
  Object.keys(grpFields).forEach(field => {
    if (field === 'TRAVELER_DOCUMENT') {
      const travellerDocument = grpFields[field].formGroups[0]?.formGroup;
      const reDocId = new RegExp(travellerDocument.DOCUMENT_ID.props.regex);
      const reDocType = new RegExp(travellerDocument.DOCUMENT_TYPE.props.regex);

      if (paxDetail[fieldNameMap['DOCUMENT_TYPE']] && !paxDetail[fieldNameMap['DOCUMENT_ID']]) {
        errors[
          fieldNameMap['DOCUMENT_ID']
        ] = `Please enter a valid ${travellerDocument.DOCUMENT_ID.label}`;
      }
      if (
        paxDetail[fieldNameMap['DOCUMENT_ID']] &&
        !reDocId.test(paxDetail[fieldNameMap['DOCUMENT_ID']])
      ) {
        errors[
          fieldNameMap['DOCUMENT_ID']
        ] = `Please enter a valid ${travellerDocument.DOCUMENT_ID.label}`;
      }
      if (
        paxDetail[fieldNameMap['DOCUMENT_TYPE']] &&
        !reDocType.test(paxDetail[fieldNameMap['DOCUMENT_TYPE']])
      ) {
        errors[
          fieldNameMap['DOCUMENT_TYPE']
        ] = `Please enter a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
      }
      if (!isOptional) {
        if (
          !paxDetail[fieldNameMap['DOCUMENT_ID']]?.length &&
          !paxDetail[fieldNameMap['DOCUMENT_TYPE']]?.length
        ) {
          errors[
            fieldNameMap['DOCUMENT_TYPE']
          ] = `Select a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
        }
      } else {
        if (
          !travellerDocument.DOCUMENT_TYPE.props.isOptional &&
          !paxDetail[fieldNameMap['DOCUMENT_TYPE']]?.length
        ) {
          errors[
            fieldNameMap['DOCUMENT_TYPE']
          ] = `Select a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
        }
        if (
          paxDetail[fieldNameMap['DOCUMENT_ID']]?.length &&
          !paxDetail[fieldNameMap['DOCUMENT_TYPE']]?.length
        ) {
          errors[
            fieldNameMap['DOCUMENT_TYPE']
          ] = `Select a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
        }
      }
      if (
        typeof paxDetail[fieldNameMap['DOCUMENT_ID']] !== 'undefined' &&
        travellerDocument.DOCUMENT_ID.props.max &&
        (paxDetail[fieldNameMap['DOCUMENT_ID']].length > travellerDocument.DOCUMENT_ID.props.max ||
          paxDetail[fieldNameMap['DOCUMENT_ID']].length > 50)
      ) {
        errors[
          fieldNameMap['DOCUMENT_ID']
        ] = `${travellerDocument.DOCUMENT_ID.label} can't be more than ${travellerDocument.DOCUMENT_ID.props.max} characters`;
        return;
      }
      if (
        typeof paxDetail[fieldNameMap['DOCUMENT_ID']] !== 'undefined' &&
        paxDetail[fieldNameMap['DOCUMENT_ID']].length < travellerDocument.DOCUMENT_ID.props.min
      ) {
        if (paxDetail[fieldNameMap['DOCUMENT_ID']].length > 0) {
          errors[
            fieldNameMap['DOCUMENT_ID']
          ] = `${travellerDocument.DOCUMENT_ID.label} can't be less than ${travellerDocument.DOCUMENT_ID.props.min} characters`;
        } else if (!travellerDocument.DOCUMENT_ID.props.isOptional) {
          errors[
            fieldNameMap['DOCUMENT_ID']
          ] = `${travellerDocument.DOCUMENT_ID.label} is mandatory`;
        }
        return;
      }
    }
  });
  return errors;
}

export const validateGstDetails = (gstDetails, gstFormDetails) => {
  let gstnErr = '';
  let cnameErr = '';
  Object.keys(gstFormDetails.rowFields).forEach(field => {
    const re = new RegExp(gstFormDetails.rowFields[field].props.regex);
    if (field === 'REGISTRATION_NO') {
      if (!re.test(gstDetails.gstn)) {
        gstnErr = 'Please enter valid gst number';
      }
    } else if (field === 'REGISTERED_COMPANY_NAME') {
      if (!re.test(gstDetails.cname)) {
        cnameErr = 'Please enter valid Company Name';
      }
    }
  });
  const hasErrors = gstnErr || cnameErr ? true : false;
  return {
    hasErrors,
    errors: {
      gstn: gstnErr,
      cname: cnameErr
    }
  };
};

export const validateContactDetails = (contactDetails, contactFormDetails) => {
  let hasErrors = false;
  let emailErrors = '';
  let mobileErrors = '';
  Object.keys(contactFormDetails.rowFields).forEach(field => {
    const re = new RegExp(contactFormDetails.rowFields[field].props.regex);
    if (field === 'EMAIL') {
      if (!contactDetails || !contactDetails.email) {
        emailErrors = 'Email ID cannot be empty';
        hasErrors = true;
      } else if (!re.test(contactDetails.email)) {
        emailErrors = 'Please enter a valid Email ID';
        hasErrors = true;
      }
    } else if (field === 'MOBILE_NUMBER') {
      if (!contactDetails || !contactDetails.mobile) {
        mobileErrors = 'Mobile Number cannot be empty';
        hasErrors = true;
      } else if (!re.test(contactDetails.mobile)) {
        mobileErrors = 'Please enter a valid mobile number';
        hasErrors = true;
      }
    }
  });
  const validationErrors = {
    hasErrors,
    errors: {
      email: emailErrors,
      mobile: mobileErrors
    }
  };
  return validationErrors;
};

export function validateField(fieldName, value, paxWiseTravellerFormDetails, paxType) {
  const paxTypeCap = paxType.toUpperCase();
  const errors = {};
  const field = Object.keys(fieldNameMap).find(key => fieldNameMap[key] === fieldName);
  const { grpFields = {} } = paxWiseTravellerFormDetails[paxTypeCap];

  if (!paxWiseTravellerFormDetails[paxTypeCap].rowFields[field]) {
    return errors;
  }

  const re = new RegExp(paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.regex);

  if (field === 'DOCUMENT_ID' || field === 'DOCUMENT_TYPE') {
    const travellerDocument = grpFields[field].formGroups[0]?.formGroup;
    const reDocId = new RegExp(travellerDocument.DOCUMENT_ID.props.regex);
    const reDocType = new RegExp(travellerDocument.DOCUMENT_TYPE.props.regex);

    if (field === 'DOCUMENT_ID') {
      if (
        (travellerDocument.DOCUMENT_ID.props.max &&
          value.length > travellerDocument.DOCUMENT_ID.props.max) ||
        value.length > 50
      ) {
        errors[
          fieldName
        ] = `${travellerDocument.DOCUMENT_ID.label} can't be more than ${travellerDocument.DOCUMENT_ID.props.max} characters`;
      }
      if (value.length < travellerDocument.DOCUMENT_ID.props.min) {
        if (value.length > 0) {
          errors[
            fieldName
          ] = `${travellerDocument.DOCUMENT_ID.label} can't be less than ${travellerDocument.DOCUMENT_ID.props.min} characters`;
        } else {
          errors[fieldName] = `${travellerDocument.DOCUMENT_ID.label} is mandatory`;
        }
      }
      if (!reDocId.test(value)) {
        errors[fieldName] = `Please enter a valid ${travellerDocument.DOCUMENT_ID.label}`;
      }
    } else {
      if (!reDocType.test(value)) {
        errors[fieldName] = `Please enter a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
      }
      if (!value?.length) {
        errors[fieldName] = `Select a valid ${travellerDocument.DOCUMENT_TYPE.label}`;
      }
    }
  } else if (
    paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max &&
    value.length > paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max
  ) {
    errors[
      fieldName
    ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} can't be more than ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.max} characters`;
  } else if (value.length < paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min) {
    if (value.length > 0) {
      errors[
        fieldName
      ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} can't be less than ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].props.min} characters`;
    } else {
      errors[
        fieldName
      ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} is mandatory.`;
    }
  } else if (value && !re.test(value)) {
    errors[
      fieldName
    ] = `Please enter valid ${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label}`;
  }
  if ((field === 'PASSPORT_ISSUING_COUNTRY' || field === 'NATIONALITY') && !value) {
    // Country field manditory
    errors[
      fieldNameMap[field]
    ] = `${paxWiseTravellerFormDetails[paxTypeCap].rowFields[field].label} is mandatory`;
  }
  return errors;
}

export function loadCss(styleSheets, disabled = false) {
  const todayTemp = new Date();
  const currentStamp = todayTemp.getTime();
  const cssTimestamp = __ENV_CONFIG__.ENV === 'prod' ? '.' + currentStamp : '';
  styleSheets.forEach(cssName => {
    if (!document.getElementById(cssName)) {
      const link = document.createElement('link');
      link.id = cssName;
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = `${__ENV_CONFIG__.CSS_HOST}${cssName}${cssTimestamp}.css`;
      link.disabled = disabled;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  });
}

export function isStylesheetLoaded(fileName) {
  const styleSheets = document.styleSheets;
  for (let i = styleSheets.length; i--; ) {
    const styleSheetsSrc = styleSheets[i].href;
    if (
      styleSheetsSrc !== null &&
      styleSheetsSrc.substr(styleSheetsSrc.length - fileName.length) === fileName
    ) {
      return true;
    }
  }
  return false;
}

export function disableCss(styleSheets) {
  styleSheets.forEach(cssName => {
    document.getElementById(cssName)?.setAttribute('disabled', '');
  });
}

export function disableCssByUrl(styleSheets) {
  styleSheets.forEach(cssName => {
    if (document.querySelector(`link[href*='${cssName}.css']`)) {
      document.querySelector(`link[href*='${cssName}.css']`).setAttribute('disabled', '');
    }
  });
}

export function enableCss(styleSheets) {
  styleSheets.forEach(cssName => {
    document.getElementById(cssName)?.removeAttribute('disabled');
  });
}

export function enableCssByUrl(styleSheets) {
  styleSheets.forEach(cssName => {
    if (document.querySelector(`link[href*='${cssName}.css']`)) {
      document.querySelector(`link[href*='${cssName}.css']`).removeAttribute('disabled');
    }
  });
}

// Old Seats -- END --

export function getPayments() {
  let count = 5;
  const success = () => {
    // sendDataToNewrelic(`new_payments_loadPaymentsjs:load:success:${count}`);
    // console.log('Payments js load success');
  };
  const error = (err, scriptTag, callback) => {
    count--;
    // gapEventTracking('paymentNewScripts', 'paymentNewScriptsFailed', err, '');
    // sendDataToNewrelic(`new_payments_loadPaymentsjs_load:error:${count}`);
    console.trace(err);
    scriptTag.parentNode.removeChild(scriptTag);
    if (count > 0) {
      callback();
    }
  };
  function loadJS() {
    const jsElm = document.createElement('script');
    jsElm.type = 'application/javascript';
    jsElm.src = `${__ENV_CONFIG__.BASE_URL}/mudra/dist/loadPayments.js?cb=${Math.random()}`;
    jsElm.onload = success;
    jsElm.onerror = () => {
      error('payments failed', jsElm, loadJS);
    };
    document.body.appendChild(jsElm);
  }
  loadJS();
}

export function seatsRepriceRequest(seat_info) {
  const seat_data = [];
  Object.keys(seat_info).forEach(key => {
    if (seat_info[key] && Array.isArray(seat_info[key]) && seat_info[key].length > 0) {
      seat_info[key].forEach(seat => {
        seat_data.push({
          source: key.split('-')[0],
          destination: key.split('-')[1],
          seat_designators: seat.c
        });
      });
    }
  });
  return {
    seat_data
  };
}

export const checkIfTwoNamesInTravellers = travellersArray => {
  for (let i = 0; i < travellersArray.length - 1; i++) {
    let traveller = travellersArray[i];
    for (let j = i + 1; j < travellersArray.length; j++) {
      const secondTraveller = travellersArray[j];
      if (
        traveller.title === secondTraveller.title &&
        traveller.givenname.toLowerCase() === secondTraveller.givenname.toLowerCase() &&
        traveller.lastname.toLowerCase() === secondTraveller.lastname.toLowerCase()
      ) {
        return true;
      }
    }
  }
  return false;
};

export function containsSameName(paxArray, pax) {
  const sameNameIndex = paxArray.findIndex(
    t =>
      t.title === pax.title &&
      t.givenname.toLowerCase() === pax.givenname.toLowerCase() &&
      t.lastname.toLowerCase() === pax.lastname.toLowerCase()
  );

  return sameNameIndex > -1;
}

export const parseTravelDetails = ({
  flightItem,
  carriers,
  aircrafts,
  date,
  airports,
  terminal,
  baggageInfo
}) => {
  return {
    flightCarrierId: carriers[flightItem.c].c,
    flightCarrierName: carriers[flightItem.c].n,
    ...(flightItem.oc > -1 && flightItem.oc !== flightItem.c
      ? { flightOperatingCarrierName: carriers[flightItem.oc].n }
      : null),
    flightNumber: flightItem.n,
    aircraft: aircrafts[flightItem.e],
    depDate: getTravelDate(date[flightItem[1]].toString()),
    arrDate: getTravelDate(date[flightItem[2]].toString()),
    departure: getDisplayTime(flightItem.d),
    arrival: getDisplayTime(flightItem.a),
    duration: getDurationForDisplay(flightItem.t),
    source: {
      city: airports[flightItem.u].t,
      iata: airports[flightItem.u].i,
      country: airports[flightItem.u].c,
      airport: airports[flightItem.u].n,
      terminal:
        terminal && flightItem.td !== -1 && airports[flightItem.td] !== '-'
          ? `Terminal ${terminal[flightItem.td]}`
          : ''
    },
    destination: {
      city: airports[flightItem.g].t,
      iata: airports[flightItem.g].i,
      country: airports[flightItem.g].c,
      airport: airports[flightItem.g].n,
      terminal:
        terminal && flightItem.ta !== -1 && terminal[flightItem.ta] !== '-'
          ? `Terminal ${terminal[flightItem.ta]}`
          : ''
    },
    freeMeal: flightItem.fm,
    baggageInfo: baggageInfo[`${carriers[flightItem.c].c}${flightItem.ns}`],
    layover: flightItem.l ? getDurationForDisplay(flightItem.l) : undefined
  };
};

export function getTravellerParams(paxDetail, paxType, token, flavour) {
  const travellers = {};
  const choose = { Mr: 1, Mrs: 2, Ms: 3, Master: 4, Miss: 3 };
  const {
    title,
    givenname,
    lastname,
    dob,
    passport_num,
    nationality,
    passport_issuing_country,
    passport_expiry,
    frequent_flier_no,
    frequent_flier_airline,
    document_id,
    document_type
  } = paxDetail;

  travellers[paxType + 'choose' + 1] = title ? choose[title] : 1;
  travellers[paxType + 'firstname' + 1] = givenname.trim();
  travellers[paxType + 'lastname' + 1] = lastname.trim();
  travellers[paxType + 'middlename' + 1] = '';

  travellers['firstname'] = givenname.trim();
  travellers['lastname'] = lastname.trim();

  if (dob) {
    travellers[paxType + 'dob' + 1] = dob.trim();
  }
  if (passport_num) {
    travellers[paxType + 'passport' + 1] = passport_num.trim();
  }
  if (nationality) {
    travellers[paxType + 'national' + 1] = nationality.trim();
  }
  if (passport_issuing_country) {
    travellers[paxType + 'passportissuingct' + 1] = passport_issuing_country.trim();
  }
  if (passport_expiry) {
    travellers[paxType + 'dateofexpiry' + 1] = passport_expiry.trim();
  }
  if (frequent_flier_no) {
    travellers[paxType + 'frequentno' + 1] = frequent_flier_no.trim();
  }
  if (frequent_flier_airline) {
    travellers[paxType + 'frequentairline' + 1] = frequent_flier_airline.trim();
  }
  if (document_id) {
    travellers[paxType + 'DocId' + 1] = document_id.trim();
  }
  if (document_type) {
    travellers[paxType + 'DocType' + 1] = document_type.trim();
  }

  const request = {
    token,
    flavour,
    ...travellers
  };

  return request;
}

export function getContactDetailsParams(contactDetails, token, flavour) {
  return {
    mobilecode: contactDetails.mobilecode?.trim() || '91',
    mobile: contactDetails.mobile.trim(),
    email: contactDetails.email.trim(),
    token,
    flavour
  };
}

const getFirstNameAndLastName = (firstNameCode, lastNameCode, firstname, title) => {
  let firstName = '',
    lastName = '';
  if (firstNameCode === 'FNU' && lastNameCode === 'F') {
    firstName = 'FNU';
    lastName = firstname || '';
  } else if (firstNameCode === 'T' && lastNameCode === 'F') {
    firstName = title || '';
    lastName = firstname || '';
  } else if (firstNameCode === 'F' && lastNameCode === 'LNU') {
    firstName = firstname || '';
    lastName = 'LNU';
  } else if (firstNameCode === 'F' && lastNameCode === 'F') {
    firstName = firstname || '';
    lastName = firstName;
  }
  return {
    firstName,
    lastName
  };
};

export const processAirlineRuleDetails = (
  noLastNameRuleDetails,
  firstname,
  title,
  buttonType,
  airlineChunk
) => {
  return {
    buttonType: buttonType,
    airlineRuleDetails: Object.keys(noLastNameRuleDetails).map(airline => {
      const firstNameCode = noLastNameRuleDetails[airline].firstName;
      const lastNameCode = noLastNameRuleDetails[airline].lastName;
      const { firstName, lastName } = getFirstNameAndLastName(
        firstNameCode,
        lastNameCode,
        firstname,
        title
      );
      let airlineImageUrl = '',
        airlineImageBackgroundColor = '';
      if (airline in airlineChunk) {
        airlineImageUrl = airlineChunk[airline].imageUrl;
        airlineImageBackgroundColor = airlineChunk[airline].color;
      } else {
        airlineImageUrl = airlineChunk['default'].imageUrl;
        airlineImageBackgroundColor = airlineChunk['default'].color;
      }
      return {
        firstName,
        lastName,
        airlineRuleMsg: noLastNameRuleDetails[airline].airlineRuleMsg,
        airlineImageUrl,
        airlineImageBackgroundColor: airlineImageBackgroundColor.replace(' ', ''),
        barcodeImageUrl: airlineChunk['barcode'].imageUrl
      };
    })
  };
};

export const checkDocumentTypeIfExists = (travellers, travellerDocumentList) => {
  if (
    !travellers ||
    travellers.length === 0 ||
    !travellerDocumentList ||
    travellerDocumentList.length === 0
  ) {
    return travellers;
  }
  return travellers.map(traveller => {
    if (traveller.document_type) {
      const isTravellerTypePresent = travellerDocumentList.find(
        typeList => typeList.key === traveller.document_type
      );
      if (isTravellerTypePresent) {
        return traveller;
      } else {
        return { ...traveller, document_id: '', document_type: '' };
      }
    }
    return traveller;
  });
};

export const contactDetailsValidationFromValidationForm = response => {
  const contactDetailsErrors = {};
  if (response.err && response.err.length) {
    const errorArray = response.err;
    for (let i = 0; i < errorArray.length; i++) {
      if (errorArray[i].em === 'MOBILE_NUMBER') {
        contactDetailsErrors['mobile'] = errorArray[i].ed;
      }
      if (errorArray[i].em === 'EMAIL') {
        contactDetailsErrors['email'] = errorArray[i].ed;
      }
    }
  }
  return contactDetailsErrors;
};

export const getFareKey = (flight, selectedFlight, roundTripFareFamilyIndex) => {
  //for one way flights
  if (selectedFlight?.bundleIndex) {
    return flight.al[selectedFlight.bundleIndex].fk;
  }
  //for round trip flights
  if (roundTripFareFamilyIndex !== null) {
    return flight.al[roundTripFareFamilyIndex].fk;
  }
  return flight.fk;
};
